import { IThunkActionCreator } from '.';
import { IPostReviewFormResponse200, postReviewForm } from '../api/review_request';

export enum EReviewRequestStatus {
  Initial,
  Loading,
  Succeed,
  Failed,
}

export interface IReviewRequestData {
  status: EReviewRequestStatus;
  data: IPostReviewFormResponse200 | null;
}

export interface IReviewRequestStatusChanged {
  type: 'IReviewRequestStatusChanged';
  status: EReviewRequestStatus;
  data?: IPostReviewFormResponse200 | null;
}

export interface IReviewRequestParameters {
  message: string;
  name: string;
  email: string;
  phoneNumber: string;
  captcha: string;
  subscribeForNews: boolean;
}

export function reviewRequest({
  message,
  captcha,
  email,
  name,
  phoneNumber,
  subscribeForNews,
}: IReviewRequestParameters): IThunkActionCreator<Promise<IPostReviewFormResponse200>> {
  return (dispatch, getState, context) => {
    dispatch<IReviewRequestStatusChanged>({
      status: EReviewRequestStatus.Loading,
      type: 'IReviewRequestStatusChanged',
    });

    const apiBaseUrl = context.config.get('apiBaseUrl') as string;
    if (!apiBaseUrl) {
      return Promise.reject('apiBaseUrl is not set');
    }

    const {
      pageData: { subdomain },
      offerData: {
        offer: { id: offerId },
      },
    } = getState();

    return postReviewForm(context.makeRequest, apiBaseUrl.replace('www', subdomain), {
      captcha,
      email,
      firstName: name,
      message,
      offerId,
      phoneNumber,
      subscribeForNews,
    })
      .then(response => {
        if (response.status !== 'ok') {
          dispatch<IReviewRequestStatusChanged>({
            data: null,
            status: EReviewRequestStatus.Failed,
            type: 'IReviewRequestStatusChanged',
          });

          throw new Error('Unexpected response status');
        }

        dispatch<IReviewRequestStatusChanged>({
          data: response,
          status: EReviewRequestStatus.Succeed,
          type: 'IReviewRequestStatusChanged',
        });

        return response;
      })
      .catch(error => {
        context.logger.error(error);

        dispatch<IReviewRequestStatusChanged>({
          data: null,
          status: EReviewRequestStatus.Failed,
          type: 'IReviewRequestStatusChanged',
        });

        throw error;
      });
  };
}

export interface IReviewRequestReset {
  type: 'IReviewRequestReset';
}

export function reset(): IReviewRequestReset {
  return {
    type: 'IReviewRequestReset',
  };
}

export type TReviewRequestActions = IReviewRequestStatusChanged | IReviewRequestReset;

const initialState = {
  data: null,
  status: EReviewRequestStatus.Initial,
};

export function reviewRequestReducer(
  state: IReviewRequestData = initialState,
  action: TReviewRequestActions,
): IReviewRequestData {
  switch (action.type) {
    case 'IReviewRequestStatusChanged':
      return {
        ...state,
        data: action.data ? action.data : state.data,
        status: action.status,
      };

    case 'IReviewRequestReset':
      return initialState;

    default:
      return state;
  }
}
